import React, { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { connect } from 'react-redux';
import WarningIcon from '../../common/icons/WarningIcon';
import RoundCheckIcon from '../../common/icons/RoundCheckIcon';
import {
  publishPresentation,
  dispatchPublishedPresentation,
} from '../../common/actions/presentationActions';
import { openViewer } from '../../common/actions/navigationActions';
import smalltalk from '../../vendor/smalltalk';
import ControlButton from '../../common/components/ControlButton';

const OverlayContent = styled.div`
  background: var(--panelColor);
  border: 2px solid var(--borderColorLight);
  padding: 8px;
  border-radius: 10px;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const PublishSection = styled.div`
  padding: 10px;
  color: var(--textColor);
  font-size: 16px;
  width: 100%;
`;

const PublishContextSection = styled.div`
  display: flex;
  align-items: center;
`;

const PublishContextIcon = styled.div`
  margin-right: 12px;
`;

const PublishActionsSection = styled.div`
  margin-top: 18px;
  display: flex;
`;

const Overlay = styled.div`
  position: relative;
  width: 350px;
  top: 5;
  margin-left: calc(50% - 175px);
  z-index: 11;
  display: ${({ visible }) => (visible ? 'block' : 'none')};

  border-radius: 10px;
  box-shadow: 0 50px 50px 0 rgba(0, 0, 0, 0.25);
`;

const PublishLastDate = styled.p`
  font-family: 'Roboto';
  font-size: 12px;
  color: var(--textSecondaryColor);
  margin-bottom: 4px;
`;

const Message = ({ isPreview, hasChangesToPublish }) => {
  if (isPreview) {
    if (hasChangesToPublish) {
      return (
        <p>
          <strong>There are some changes to publish</strong>
        </p>
      );
    }
    return (
      <p>
        <strong>All changes are published!</strong>
      </p>
    );
  }
  if (hasChangesToPublish) {
    return (
      <p>
        <strong>There are some changes to publish</strong>
      </p>
    );
  }
  return (
    <p>
      <strong>All changes are published!</strong>
    </p>
  );
};

Message.propTypes = {
  isPreview: PropTypes.bool.isRequired,
  hasChangesToPublish: PropTypes.bool.isRequired,
};

const PublishOverlay = ({
  visible,
  hasChangesToPublish,
  isPreview,
  ...props
} = {}) => {
  const [isPublishing, setPublishing] = useState(false);
  const publishedAt = props.publishedAt ? props.publishedAt : null;

  const openPreview = () => {
    props.openViewer(window.presentationId, 1, window.presentationId);
  };

  const openPublic = () => {
    props.openViewer(
      window.presentationId,
      1,
      window.presentationId,
      undefined,
      true,
    );
  };

  const onPublish = () => {
    setPublishing(true);
    props
      .publishPresentation(window.presentationId)
      .then(() => {
        setPublishing(false);
        props.dispatchPublishedPresentation();
      })
      .catch(e => {
        setPublishing(false);
        smalltalk.alert(
          'Oops',
          `An error occurred while publishing the presentation. <code>${JSON.stringify(
            e,
          )}</code>`,
        );
      });
  };

  return (
    <Overlay visible={visible} {...props}>
      <OverlayContent>
        <PublishSection>
          <PublishContextSection>
            <PublishContextIcon>
              {hasChangesToPublish ? (
                <WarningIcon height={30} color="var(--secondaryColor)" />
              ) : (
                <RoundCheckIcon height={20} color="var(--primaryColor)" />
              )}
            </PublishContextIcon>

            <div>
              <Message
                isPreview={isPreview}
                hasChangesToPublish={hasChangesToPublish}
              />
              {publishedAt && (
                <PublishLastDate>
                  Last publish: {formatDistanceToNow(new Date(publishedAt))} by
                  yourself
                </PublishLastDate>
              )}
            </div>
          </PublishContextSection>
          {(!isPreview && !hasChangesToPublish) || (
            <PublishActionsSection>
              {isPreview ? (
                <ControlButton
                  style={{ width: hasChangesToPublish ? 'auto' : '100%' }}
                  variant="transparent"
                  id="public-version-button"
                  onClick={openPreview}
                >
                  Published version
                </ControlButton>
              ) : (
                <ControlButton
                  id="preview-version-button"
                  style={{ width: '100%' }}
                  variant="blueNoBorder"
                  onClick={openPublic}
                >
                  Draft version
                </ControlButton>
              )}
              {isPreview && hasChangesToPublish && (
                <ControlButton
                  id="publish-changes-button"
                  variant="blueNoBorder"
                  style={{
                    flexGrow: 1,
                    pointerEvents: isPublishing ? 'none' : 'auto',
                    opacity: isPublishing ? '0.5' : '1',
                    marginLeft: '10px',
                  }}
                  onClick={onPublish}
                >
                  Publish changes
                </ControlButton>
              )}
            </PublishActionsSection>
          )}
        </PublishSection>
      </OverlayContent>
    </Overlay>
  );
};

const mapStateToProps = state => {
  return {
    hasChangesToPublish: state.slideReducer.hasChangesToPublish,
    publishedAt: state.slideReducer.publishedAt,
    isPreview: Boolean(
      new URLSearchParams(window.location.search).get('draft') === '1',
    ),
  };
};

export default connect(mapStateToProps, {
  publishPresentation,
  dispatchPublishedPresentation,
  openViewer,
})(PublishOverlay);

PublishOverlay.propTypes = {
  visible: PropTypes.bool.isRequired,
  publishPresentation: PropTypes.func.isRequired,
  dispatchPublishedPresentation: PropTypes.func.isRequired,
  openViewer: PropTypes.func.isRequired,
  hasChangesToPublish: PropTypes.bool.isRequired,
  publishedAt: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  isPreview: PropTypes.bool.isRequired,
};
